import {Component, Input, OnInit } from '@angular/core';
import { ApplicationRoles, SessionService, User } from '@edl-development/edl-common';
import { ActionName } from 'src/app/enums/action-name.enum';
import { CplItem } from 'src/app/model/cpl-item.model';
import { CplItemPermissions } from 'src/app/model/enum/cpl-item-permissions.enum';
import { ComplianceItemActionService } from 'src/app/services/compliance-item-action.service';
import { ComplianceItemHelpersService } from 'src/app/services/compliance-item-helpers/compliance-item-helpers.service';

@Component({
    selector:    'compliance-item-action-button',
    templateUrl: './compliance-item-action-button.component.html'
})
export class ComplianceItemActionButtonComponent implements OnInit {
    @Input() actionName: ActionName;
    @Input() complianceItem: CplItem;
    @Input() shouldRefresh: boolean = false;
    @Input() isDropdownItem: boolean = true;
    @Input() customClasses: { [key: string]: any } = {};

    user: User;

    constructor(
        private complianceItemActionService: ComplianceItemActionService,
        private complianceItemHelpersService: ComplianceItemHelpersService,
        private sessionService: SessionService
    ) {}
    
    ngOnInit() {
        this.user = this.sessionService.user;
    }

    isLink() {
        switch(this.actionName) {
            case ActionName.VIEW:
            case ActionName.EDIT:
                return true;
            default:
                return false;
        }
    }

    getUISref() {
        switch(this.actionName) {
            case ActionName.VIEW:
                return "authenticated.compliance-items.compliance-item.roster";
            case ActionName.EDIT:
                return 'authenticated.compliance-items.compliance-item.edit';
            default:
                return null;
        }
    }

    onClick() {
        this.complianceItemActionService[this.actionName](
            this.complianceItem
        )
    }

    getClasses() {
        return {
            'btn': !this.isDropdownItem,
            'btn-info': !this.isDropdownItem,
            'dropdown-item': this.isDropdownItem,
            'd-none': this.hideOnMobile(),
            'd-lg-block': this.hideOnMobile(),
            ...this.customClasses
        }
    }

    hideOnMobile() {
        switch(this.actionName) {
            case ActionName.EDIT:
            case ActionName.CONVERT_TO_TEMPLATE:
            case ActionName.DUPLICATE:
            case ActionName.ARCHIVE:
            case ActionName.VIEW:
                return true;
            case ActionName.REQUEST_ACCESS:
            case ActionName.RESTORE:
            case ActionName.ACTIVATE:
            case ActionName.DELETE:
            default:
                return false;
        }
    }

    getAccessControl() {
        switch(this.actionName) {
            case ActionName.VIEW:
            case ActionName.DUPLICATE:
            case ActionName.REQUEST_ACCESS:
                return [ApplicationRoles.EDLADMIN, ApplicationRoles.ADMIN, ApplicationRoles.SUPERVISOR, ApplicationRoles.LIMITED_SUPERVISOR];
            case ActionName.CONVERT_TO_TEMPLATE:
            case ActionName.REMOVE:
                return [ApplicationRoles.EDLADMIN];
            case ActionName.EDIT:
            case ActionName.RESTORE:
            case ActionName.DELETE:
                return this.complianceItem.IsTemplate ? [ApplicationRoles.EDLADMIN] : [ApplicationRoles.OWNER, ApplicationRoles.ADMIN, ApplicationRoles.EDLADMIN];
            case ActionName.ARCHIVE:
            case ActionName.ACTIVATE:
            default:
                return [ApplicationRoles.ADMIN, ApplicationRoles.EDLADMIN];
        }
    }

    shouldShowButton() {
        const canEditItem = this.complianceItemHelpersService.canEditItem(this.complianceItem); 
        const canEditDeletedItem = this.complianceItemHelpersService.canEditDeletedItem(this.complianceItem);

        switch(this.actionName) {
            case ActionName.EDIT:
                return canEditItem;
            case ActionName.RESTORE:
                return canEditDeletedItem && this.complianceItem.IsDeleted;
            case ActionName.REMOVE:
                return canEditDeletedItem && this.complianceItem.IsDeleted;
            case ActionName.ACTIVATE:
                return canEditItem && this.complianceItem.IsArchived && !this.complianceItem.IsTemplate;
            case ActionName.ARCHIVE:
                return canEditItem && !this.complianceItem.IsArchived && !this.complianceItem.IsDeleted && !this.complianceItem.IsTemplate;
            case ActionName.DELETE:
                return canEditItem && !this.complianceItem.IsDeleted && (this.complianceItem.IsTemplate || (!this.complianceItem.IsPreview && !this.complianceItem.IsPublished));
            case ActionName.CONVERT_TO_TEMPLATE:
                return !this.complianceItem.IsDeleted && !this.complianceItem.IsTemplate;
            case ActionName.DUPLICATE:
                return !this.complianceItem.IsDeleted && this.complianceItem.CanView;
            case ActionName.VIEW:
                return this.complianceItem.CanView
            case ActionName.REQUEST_ACCESS:
                return (this.complianceItem?.Permissions ?? []).includes(CplItemPermissions.CAN_REQUEST_ACCESS);
            default:
                return true
        }
    }

    getLabel() {
        switch(this.actionName) {
            case ActionName.EDIT:
                return 'Edit';
            case ActionName.RESTORE:
                return 'Restore';
            case ActionName.REMOVE:
                return 'Remove';
            case ActionName.ACTIVATE:
                return 'Activate';
            case ActionName.ARCHIVE:
                return 'Archive';
            case ActionName.CONVERT_TO_TEMPLATE:
                return 'Convert to Template';
            case ActionName.DELETE:
                return 'Delete';
            case ActionName.DUPLICATE:
                return 'Duplicate';
            case ActionName.VIEW:
                return 'View';
            case ActionName.REQUEST_ACCESS:
                return 'Request Access';
            default:
                return 'Button';
        }
    }
}